<script setup lang="ts" generic="T">
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import DropdownMultiSelectActionDialog from '@/core/dialogs/DropdownMultiSelectActionDialog.vue';
import DropdownSubmenuActionDialog from '@/core/dialogs/DropdownSubmenuActionDialog.vue';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import ClickableListItem from '@/core/elements/ClickableListItem.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import { IDropdownListAction } from '@/interfaces';
import { hasMenuSeparatorBefore } from '@/utils/menu.utils';
import { ComponentPublicInstance, ref, toRefs } from 'vue';
import BillingProBadge from '../billing/BillingProBadge.vue';
import { useBillingState } from '@/composables/billing/useBillingState';

const emit = defineEmits<{
  'click:option': [];
  'request-upgrade': [action: IDropdownListAction<T>];
}>();

const props = defineProps<{
  alwaysVisible?: boolean;
  actions: IDropdownListAction<T>[];
  data?: T;
}>();
const { data } = toRefs(props);

const { hasFeature } = useBillingState();

const anchorButtonRefs = ref<HTMLElement[]>([]);
const multiSelectActionDialogRefs = ref<InstanceType<typeof DropdownMultiSelectActionDialog>[]>([]);
const submenuActionDialogRefs = ref<InstanceType<typeof DropdownSubmenuActionDialog>[]>([]);

const isLocked = (a: IDropdownListAction<T>): boolean => (a.requiredFeature ? !hasFeature(a.requiredFeature) : false);

const handleActionClick = (action: IDropdownListAction<T>, index: number, event?: Event): void => {
  const locked = isLocked(action);
  const behavior = action.lockedBehavior ?? 'emit';

  if (locked) {
    if (behavior === 'emit') emit('request-upgrade', action);
    return;
  }

  if (action.multiSelectAction) {
    event?.stopPropagation();
    multiSelectActionDialogRefs.value?.[index]?.show();
  } else if (action.submenuAction) {
    event?.stopPropagation();
    submenuActionDialogRefs.value?.[index]?.show();
  } else {
    void action.action?.(data.value);
    emit('click:option');
  }
};

const setAnchorButtonRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  if (!el) return;
  anchorButtonRefs.value[index] = el as HTMLElement;
};

const setMultiSelectActionDialogRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  if (!el) return;
  multiSelectActionDialogRefs.value[index] = el as InstanceType<typeof DropdownMultiSelectActionDialog>;
};

const setSubmenuActionDialogRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  if (!el) return;
  submenuActionDialogRefs.value[index] = el as InstanceType<typeof DropdownSubmenuActionDialog>;
};
</script>

<template>
  <div
    v-for="(action, index) of actions"
    :key="`${action.label}-${index}`"
    :ref="(el) => setAnchorButtonRef(el, index)"
  >
    <CustomSeparator v-if="hasMenuSeparatorBefore(actions, index)" class="my-sm" />
    <ClickableListItem
      v-if="!action.hidden"
      tabindex="0"
      class="p-sm outline-inner flex justify-between"
      :class="{
        'text-error': action.style === 'error',
        'opacity-50': action.disabled,
        'pointer-events-none':
          action.disabled || (action.action == null && !action.multiSelectAction && !action.submenuAction),
      }"
      @click="handleActionClick(action, index, $event)"
    >
      <div class="flex gap-sm flex-1 items-center">
        <AnyIcon v-if="action.iconBefore" :icon-name="action.iconBefore" :class="['size-icon flex-shrink-0']" />
        <span class="truncate">{{ action.label }}</span>
        <component :is="action.iconAfter" v-if="action.iconAfter" :class="['ml-auto']" />
        <ChevronRightIcon v-if="action.multiSelectAction || action.submenuAction" class="size-icon-sm flex-shrink-0" />
        <DropdownMultiSelectActionDialog
          v-if="action.multiSelectAction && anchorButtonRefs?.[index]"
          :ref="(el) => setMultiSelectActionDialogRef(el, index)"
          :action="action.multiSelectAction"
          :anchor-button-ref="anchorButtonRefs?.[index]"
          @click:action="emit('click:option')"
        />
        <DropdownSubmenuActionDialog
          v-if="action.submenuAction && anchorButtonRefs?.[index]"
          :ref="(el) => setSubmenuActionDialogRef(el, index)"
          :action="action.submenuAction"
          :label="typeof action.label === 'function' ? action.label(data as T) : action.label"
          :anchor-button-ref="anchorButtonRefs?.[index]"
          @click:action="emit('click:option')"
        />
      </div>
      <BillingProBadge v-if="isLocked(action)" :theme="'dark'" />
    </ClickableListItem>
  </div>
  <div v-if="actions.length === 0 || actions.every((a) => a.hidden)" class="p-sm text-center text-secondary">
    {{ $t('general.noActions') }}
  </div>
</template>
